import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, TitleStrategy, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { PageTitleStrategy } from './page-title.strategy';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import { environment } from '../environments/environment';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { provideHttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';

export const appConfig: ApplicationConfig = {
  providers: [
    // Config Firebase
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => {
      // Initialize Firestore with explicit transport detection to fix the first-save
      // hang on Chrome/macOS (BS-491). With the default bare getFirestore(), the SDK
      // lazily negotiates a streaming WebChannel on the first RPC of the session; on
      // Chrome/macOS (QUIC/HTTP3 + proxy/VPN/SSL-inspection buffering) that bidi-stream
      // probe can stall for seconds before falling back to long-polling, so only the
      // first save hangs. experimentalAutoDetectLongPolling lets the SDK choose
      // long-polling from the first response instead of hanging on the probe.
      // Escalate to experimentalForceLongPolling: true if BS-490 shows auto-detect
      // still stalls on the affected machine.
      const firestore = initializeFirestore(getApp(), {
        experimentalAutoDetectLongPolling: true,
      });
      if (environment.useEmulators && environment.emulators?.firestore) {
        connectFirestoreEmulator(
          firestore,
          environment.emulators.firestore.host,
          environment.emulators.firestore.port,
        );
      }
      return firestore;
    }),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators && environment.emulators?.auth) {
        connectAuthEmulator(
          auth,
          `http://${environment.emulators.auth.host}:${environment.emulators.auth.port}`,
        );
      }
      return auth;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators && environment.emulators?.storage) {
        connectStorageEmulator(
          storage,
          environment.emulators.storage.host,
          environment.emulators.storage.port,
        );
      }
      return storage;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulators && environment.emulators?.functions) {
        connectFunctionsEmulator(
          functions,
          environment.emulators.functions.host,
          environment.emulators.functions.port,
        );
      }
      return functions;
    }),

    provideHttpClient(),
    provideAnimations(),

    // Default Angular
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
    ),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    provideClientHydration(withEventReplay()),
  ],
};
